import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Bold,
  ChevronsUpDown,
  CircleUser,
  Highlighter,
  Italic,
  List,
  Pin,
  PinOff,
  Shuffle,
  SmilePlus,
  Strikethrough,
  Trash2,
  Type,
  Underline,
  type LucideIcon,
} from 'lucide-react'
import {
  DEFAULT_CANVAS_NOTE_COLOR,
  getCanvasNoteBarColors,
  getCanvasNoteColor,
} from '../utils/canvasNoteStyle'
import { getCanvasNoteFont } from '../utils/canvasFonts'
import { CanvasNoteDrop } from './CanvasNoteDrop'
import { CanvasColorPicker } from './CanvasColorPicker'
import { CanvasApplyTasksIcon } from '../icons/CanvasApplyTasksIcon'
import { usePresence } from '../hooks/usePresence'
import { CanvasFontMenu } from './CanvasFontMenu'
import { CanvasTextSizeMenu } from './CanvasTextSizeMenu'
import { CanvasTextAlignMenu, CANVAS_TEXT_ALIGNS } from './CanvasTextAlignMenu'
import { canvasTextSizePreset, getCanvasTextHighlight } from '../utils/canvasText'
import type { CanvasTextMarkKind } from '../utils/canvasTextFormat'
import type { ButtonShortcut } from '@/src/renderer/components/ui/Button/Button.types'
import { OPTIONS_BAR_REPOSITION_EVENT } from './CanvasOptionsBar.types'
import type {
  CanvasOptionsBarButton,
  CanvasOptionsBarPlacement,
  CanvasOptionsBarProps,
} from './CanvasOptionsBar.types'
import './CanvasOptionsBar.scss'
import { WithTooltip } from '@/src/renderer/components/ui/tooltip'

/** Welcher Aufklapper offen ist. */
type OpenFlyout = 'color' | 'font' | 'size' | 'highlight' | 'align' | null

/** Die Aufklapper mit Vorschau beim Überfahren. */
type PreviewFlyout = 'color' | 'highlight' | 'font'
/** Ein Vorschau-Rückruf, gleich welcher Art — die Leiste reicht nur durch. */
type PreviewCallback = (preview: object | null | 'picked') => void

/** Die vier Umschalter des Texts, mit den Kürzeln, die im Schreibfeld gelten (TipTap). */
const MARK_BUTTONS: readonly { mark: CanvasTextMarkKind; icon: LucideIcon; shortcut: ButtonShortcut }[] = [
  { mark: 'bold', icon: Bold, shortcut: { kind: 'character', text: 'B', mod: true } },
  { mark: 'italic', icon: Italic, shortcut: { kind: 'character', text: 'I', mod: true } },
  { mark: 'underline', icon: Underline, shortcut: { kind: 'character', text: 'U', mod: true } },
  { mark: 'strike', icon: Strikethrough, shortcut: { kind: 'character', text: 'S', mod: true, shift: true } },
]

/** Der Pfeil aus dem Entwurf: ein einzelner Winkel nach unten, 7 × 4, Strich 1,5. Auch im Titelwerkzeug. */
export const Caret: React.FC = () => (
  <svg className="canvas-options-bar__caret" width="7" height="4" viewBox="0 0 7 4" fill="none" aria-hidden="true" focusable="false">
    <path d="M5.75 0.75L3.25 2.89286L0.75 0.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

/**
 * Die **Standard**-Tinte eines Texts in der Leiste — ein eigener Schlüssel,
 * damit „Standard" und „uneinheitlich" nicht beide `null` heißen.
 */
const INK_DEFAULT = '__ink-default__'

/** Der Tropfen der Standard-Tinte: halb dunkel, halb hell — sie folgt dem Theme. */
const INK_DEFAULT_FILL = ['#0a0a0a', '#fafafa'] as const

const NO_SIZES: readonly number[] = []

/** Der Abstand eines Aufkläppers zur Leiste (Figma). */
const FLYOUT_GAP = 8

/** Der Abstand, den die Leiste zu einem Rand oder einem anderen Bedienelement hält. */
const EDGE_GAP = 8

/**
 * Die schwebenden Bedienelemente des Canvas, denen die Leiste ausweicht — die
 * **Werkzeug-Pille** am linken Rand und die **Aktionsgruppe** oben rechts.
 *
 * Beide sind Bedienung und liegen über allem, was auf dem Canvas liegt
 * (CONTEXT.md, „Werkzeug-Pille"): Eine Leiste, die unter ihnen verschwindet,
 * wäre nicht nur halb verdeckt, sondern zur Hälfte nicht mehr zu treffen.
 */
const OBSTACLES = '.canvas-toolbar, .project-canvas-panel__actions'

/**
 * Die eindeutigen Werte einer Auswahl, in der Reihenfolge ihres ersten
 * Auftretens — die Grundlage für „einheitlich oder nicht".
 */
function distinct<T>(values: readonly T[]): T[] {
  return values.filter((v, i) => values.indexOf(v) === i)
}

/**
 * Wie weit ein Kasten waagerecht rücken muss, um frei von den Hindernissen und
 * den Rändern des Canvas zu stehen — für die Leiste wie für ihren Aufklapper.
 */
function clearanceNudge(box: DOMRect, canvas: Element | null): number {
  const bounds = canvas
    ? canvas.getBoundingClientRect()
    : { left: 0, right: window.innerWidth }

  let left = bounds.left + EDGE_GAP
  let right = bounds.right - EDGE_GAP

  for (const element of canvas?.querySelectorAll(OBSTACLES) ?? []) {
    const obstacle = element.getBoundingClientRect()
    if (obstacle.width === 0) continue
    // Nur was auf derselben Höhe liegt, steht überhaupt im Weg.
    if (obstacle.bottom <= box.top || obstacle.top >= box.bottom) continue
    // Auf welcher Seite es steht, entscheidet die Mitte — nicht die Kante:
    // Ein Element, das den Kasten bereits überlappt, hätte sonst beide.
    if (obstacle.left + obstacle.width / 2 < box.left + box.width / 2) {
      left = Math.max(left, obstacle.right + EDGE_GAP)
    } else {
      right = Math.min(right, obstacle.left - EDGE_GAP)
    }
  }

  /*
    Links hat Vorrang, wenn der Platz für beides nicht reicht: Die
    Werkzeug-Pille steht links und ist ständig in Gebrauch, der rechte Rand
    schneidet höchstens ein Stück ab.
  */
  let dx = 0
  if (box.right > right) dx = right - box.right
  if (box.left + dx < left) dx = left - box.left
  return Math.round(dx)
}

/**
 * Die **Options-Leiste** über einem ausgewählten **Canvas-Element** — oder,
 * ab zwei Elementen, über dem **Auswahlrahmen** (Figma `5081:46414` für die
 * Memo, `5083:51764` für die Notiz).
 *
 * **Sie trägt die Farbe ihres Elements**: Fläche, Trenner, Beschriftung und der
 * Farb-Tropfen kommen aus der Familie des Zettels darunter. Gilt für die
 * Auswahl keine gemeinsame Farbe, wird sie neutral — eine Auswahl hat keine
 * eigene Farbe.
 *
 * **Sie behauptet nichts, was nicht für alle gilt.** Bei uneinheitlichen
 * Werten zeigt der Tropfen zwei der vorhandenen Töne diagonal geteilt und der
 * Schrift-Knopf „Mehrere".
 *
 * Sie ist ein **Mauswerkzeug**: ⇥ gehört auf dem Canvas der Tab-Reihenfolge und
 * führt nicht in die Leiste (siehe CONTEXT.md, „Options-Leiste").
 *
 * @example
 * <CanvasOptionsBar
 *   buttons={['emoji', 'color', 'font', 'author', 'delete']}
 *   colorKeys={[note.color]}
 *   fontKeys={[note.font]}
 *   onColorChange={…} onFontChange={…} onDelete={…}
 * />
 */
export const CanvasOptionsBar: React.FC<CanvasOptionsBarProps> = ({
  buttons,
  colorKeys,
  fontKeys,
  colorKind = 'surface',
  fontSizes = NO_SIZES,
  titleOn = false,
  authorOn = false,
  placement = 'above',
  onColorChange,
  onFontChange,
  onFontSizeChange,
  onColorReset,
  onColorPreview,
  onHighlightPreview,
  onFontPreview,
  textFormat,
  onTextFormat,
  onToggleTitle,
  onToggleAuthor,
  onDelete,
  onTape,
  onRetape,
  onUntape,
  onApply,
  applyState = 'ready',
  applyCount = 1,
  onActionDone,
  leaving = false,
  className,
}) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState<OpenFlyout>(null)
  // Die Aufklapper kommen und gehen wie die Leiste selbst — sanft.
  const colorPresence = usePresence(open === 'color')
  const fontPresence = usePresence(open === 'font')
  const sizePresence = usePresence(open === 'size')
  const highlightPresence = usePresence(open === 'highlight')
  const alignPresence = usePresence(open === 'align')
  const rootRef = useRef<HTMLDivElement>(null)
  const flyoutRef = useRef<HTMLDivElement>(null)
  /*
    Wohin der offene Aufklapper geht. Er folgt der Leiste — aber nur, solange
    er dort auch hinpasst: Das **Schriftmenü** ist mit acht Einträgen rund
    270px hoch, die Leiste selbst 34px. Über einem Zettel im oberen Drittel hat
    die Leiste also Platz, ihr Menü aber nicht, und es wurde am Rand
    abgeschnitten.
  */
  const [flyoutPlacement, setFlyoutPlacement] = useState<CanvasOptionsBarPlacement>(placement)

  const ink = colorKind === 'ink'
  const colors = useMemo(
    () => distinct(colorKeys.map(k => (ink && !k ? INK_DEFAULT : getCanvasNoteColor(k).key))),
    [colorKeys, ink],
  )
  const sizes = useMemo(() => distinct(fontSizes), [fontSizes])
  const uniformSize = sizes.length === 1 ? sizes[0]! : undefined
  const fonts = useMemo(() => distinct(fontKeys.map(k => k ?? null)), [fontKeys])

  const uniformColor = colors.length === 1 ? colors[0]! : null
  const uniformFont = fonts.length === 1 ? fonts[0]! : undefined

  // Die Leiste folgt der FAMILIE: Helles und dunkles Grün färben sie grün,
  // auch wenn die Stufen gemischt sind. Erst verschiedene Farben machen sie
  // neutral.
  // Die Standard-Tinte hat keinen Ton — mit ihr ist die Leiste neutral.
  const hues = useMemo(() => distinct(colors.map(k => (k === INK_DEFAULT ? null : getCanvasNoteColor(k).hue))), [colors])
  const bar = getCanvasNoteBarColors(hues.length === 1 ? hues[0]! : null)

  /*
    Der Tropfen zeigt den Ton des Zettels — nicht den kräftigeren Wählerton:
    Er ist eine Anzeige und keine Wahl. Bei mehreren Farben zwei davon,
    diagonal geteilt (FigJam-Muster).
  */
  // Beim Text zeigt er die SCHRIFTfarbe — den Ton, den der Wähler anbietet.
  const fillOf = (key: string) => (key === INK_DEFAULT
    ? INK_DEFAULT_FILL[0]
    : ink ? getCanvasNoteColor(key).swatch : getCanvasNoteColor(key).hex)
  const chipFill: string | readonly [string, string] = colors.length > 1
    ? [fillOf(colors[0]!), fillOf(colors[1]!)] as const
    : uniformColor === INK_DEFAULT
      ? INK_DEFAULT_FILL
      : fillOf(uniformColor ?? DEFAULT_CANVAS_NOTE_COLOR)

  const fontLabel = uniformFont === undefined
    ? t('projectCanvas.note.fontMixed')
    : getCanvasNoteFont(uniformFont).label
  /*
    Der Name steht in seiner eigenen Schrift — mit demselben optischen
    Ausgleich wie der Zettel, sonst stünde „Caveat" im Knopf halb so groß wie
    „Lora" daneben.
  */
  const fontStyle = uniformFont === undefined
    ? undefined
    : {
      fontFamily: getCanvasNoteFont(uniformFont).stack,
      fontWeight: getCanvasNoteFont(uniformFont).weight,
      fontSize: `calc(14px * ${getCanvasNoteFont(uniformFont).scale})`,
    }

  // Der Knopf nennt die Stufe, sonst die Zahl, sonst „Gemischt" (FigJam-Muster).
  const sizePreset = canvasTextSizePreset(uniformSize)
  const sizeLabel = uniformSize === undefined
    ? t('projectCanvas.text.sizeMixed')
    : sizePreset ? t(`projectCanvas.text.size.${sizePreset}`) : String(uniformSize)

  /*
    ── Die Vorschau der Aufklapper ──────────────────────────────────────────

    Farbwähler, Marker und Schriftmenü melden nur Betreten und Verlassen
    ihrer Einträge. Das Ende
    beim Schließen meldet die Leiste — gleich, WIE geschlossen wird (Escape,
    Klick daneben, Knopf, Abwählen des Elements). Nach einem Klick kommt
    `'picked'` statt `null`, und zwar VOR dem Schreiben: Der Text nimmt seine
    Vorschau im Editor dann erst zurück, bevor der echte Befehl läuft.

    Refs statt Zustand: Die Meldungen dürfen kein Rendern der Leiste
    auslösen, und das Aufräumen beim Unmount braucht die letzten Rückrufe.
  */
  const openRef = useRef(open)
  openRef.current = open
  const previewingRef = useRef<PreviewFlyout | null>(null)
  const previewCallbacksRef = useRef<Record<PreviewFlyout, PreviewCallback | undefined>>({ color: undefined, highlight: undefined, font: undefined })
  previewCallbacksRef.current = {
    color: onColorPreview as PreviewCallback | undefined,
    highlight: onHighlightPreview as PreviewCallback | undefined,
    font: onFontPreview as PreviewCallback | undefined,
  }

  const endPreview = useCallback((how: null | 'picked') => {
    const which = previewingRef.current
    if (!which) return
    previewingRef.current = null
    previewCallbacksRef.current[which]?.(how)
  }, [])

  // Der Wähler blendet nach dem Schließen noch aus — was er dabei meldet, gilt nicht mehr.
  const previewFrom = useCallback((which: PreviewFlyout) => (choice: object | null) => {
    if (openRef.current !== which) return
    if (choice === null) {
      endPreview(null)
      return
    }
    previewingRef.current = which
    previewCallbacksRef.current[which]?.(choice)
  }, [endPreview])
  const colorPreviewHandler = useMemo(() => previewFrom('color'), [previewFrom])
  const highlightPreviewHandler = useMemo(() => previewFrom('highlight'), [previewFrom])
  const fontPreviewHandler = useMemo(() => previewFrom('font'), [previewFrom])

  // Vor dem Schreiben — siehe oben.
  const pickDone = useCallback(() => {
    openRef.current = null
    endPreview('picked')
  }, [endPreview])

  useLayoutEffect(() => {
    if (previewingRef.current && previewingRef.current !== open) endPreview(null)
  }, [open, endPreview])
  useEffect(() => () => endPreview(null), [endPreview])

  /*
    **Freie Sicht auf die Vorschau:** Solange Farbwähler, Marker oder
    Schriftmenü offen sind, trägt die Pinnwand `data-previewing`, und das SCSS
    nimmt die Auswahl-Anzeige ab (`CanvasFlow.scss`). Ein Datenattribut, keine
    Klasse: `className` gehört React und würde beim nächsten Rendern
    überschrieben. Es gibt immer nur eine Leiste — der Merker gilt der ganzen
    Pinnwand.
  */
  useLayoutEffect(() => {
    if (open !== 'color' && open !== 'highlight' && open !== 'font') return
    const flow = rootRef.current?.closest('.canvas-flow')
    if (!flow) return
    flow.setAttribute('data-previewing', '')
    return () => flow.removeAttribute('data-previewing')
  }, [open])

  const toggle = useCallback((which: Exclude<OpenFlyout, null>) => {
    setOpen(current => (current === which ? null : which))
  }, [])

  /*
    ── Die waagerechte Ausweichbewegung ──────────────────────────────────────

    `NodeToolbar` setzt die Leiste mittig über den Zettel und kennt nichts
    außer ihm. Über einem Zettel am linken Rand schiebt sie sich dadurch unter
    die **Werkzeug-Pille** — und die liegt oben, also wäre die halbe Leiste
    nicht mehr zu treffen.

    Sie rückt deshalb so weit zur Seite, dass sie frei steht. **Ohne React-
    Zustand**, direkt als CSS-Variable auf das Element: Die Lage ändert sich
    beim Zoomen laufend, und ein Zustand daraus machte aus jedem Rad-Tick ein
    Rendern.
  */
  const avoidObstacles = useCallback(() => {
    const bar = rootRef.current
    if (!bar) return

    // Erst zurück auf null, dann messen: Sonst würde die eigene Verschiebung
    // aus dem letzten Durchgang mitgemessen und die Leiste liefe davon.
    bar.style.setProperty('--bar-nudge', '0px')
    const box = bar.getBoundingClientRect()
    // Im inaktiven Tab misst alles 0 (TabStack-Falle, CLAUDE.md).
    if (box.width === 0) return

    const canvas = bar.closest('.react-flow')
    bar.style.setProperty('--bar-nudge', `${clearanceNudge(box, canvas)}px`)

    /*
      Der offene Aufklapper danach, in der schon verschobenen Leiste: Der
      Farbwähler ist viel breiter als sein Knopf und steht mittig über ihm —
      vorn in der Leiste ragt er links über sie hinaus, und die Leiste stünde
      frei, während ihre Farben unter der Pille lägen. Hier und nicht in einem
      eigenen Effekt, damit er bei jeder Bewegung der Leiste mitgeht.
    */
    const flyout = flyoutRef.current
    if (!flyout) return
    flyout.style.setProperty('--flyout-nudge', '0px')
    const flyoutBox = flyout.getBoundingClientRect()
    if (flyoutBox.width === 0) return
    flyout.style.setProperty('--flyout-nudge', `${clearanceNudge(flyoutBox, canvas)}px`)
  }, [])

  /*
    Nach jedem Zeichnen — Auswahl, Farbwechsel, ein geöffneter Aufklapper, der
    die Leiste breiter macht.
  */
  useLayoutEffect(avoidObstacles)

  /*
    Und außerdem beim Verändern der Fenstergröße.

    **Nicht am Rad**, obwohl sich die Lage dabei ändert: Messen heißt hier
    Layout erzwingen (ein Stilschreiben, dann `getBoundingClientRect`), und das
    über die ganze Canvas-Fläche in jedem Frame eines Zuges war genau der
    Grund, warum sich das Fahren des Ausschnitts zäh anfühlte. Während des
    Zuges ist die Leiste jetzt ohnehin ausgeblendet (`useCanvasPointerBusy`
    zählt das Rad mit) — sie kommt danach frisch und misst sich beim Aufbau.
  */
  useEffect(() => {
    const onResize = () => avoidObstacles()
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [avoidObstacles])

  /*
    Und wenn jemand die Hülle verschiebt, ohne dass die Leiste neu zeichnet —
    der Text, während seine neue Lage noch nicht geschrieben ist (gemessen
    2026-09-23: Die Leiste maß sich im Übergang doppelt verschoben unter der
    Werkzeug-Pille und blieb 83px daneben stehen). Das Signal kommt
    synchron, die Korrektur steht also im selben Bild.
  */
  useEffect(() => {
    const toolbar = rootRef.current?.closest('.react-flow__node-toolbar')
    if (!toolbar) return
    const onReposition = () => avoidObstacles()
    toolbar.addEventListener(OPTIONS_BAR_REPOSITION_EVENT, onReposition)
    return () => toolbar.removeEventListener(OPTIONS_BAR_REPOSITION_EVENT, onReposition)
  }, [avoidObstacles])

  /*
    Die Kollisionsprüfung der Aufkläpper — vor dem Zeichnen
    (`useLayoutEffect`), damit einer nicht erst falsch erscheint und dann
    springt.

    Gemessen wird gegen die FLÄCHE DES CANVAS, nicht gegen das Fenster: Im
    geteilten Modus endet der Canvas vor dem Fensterrand, und ein Menü, das
    unter der Aufgabenliste weiterläuft, ist genauso abgeschnitten. Ohne
    Canvas (Storybook) gilt das Fenster.

    Die Richtung der Leiste hat Vorrang; gewechselt wird nur, wenn es dort
    nicht passt UND auf der anderen Seite passt. Passt es nirgends, bleibt es,
    wo es hingehört — dann ist die Fläche zu klein für beides.
  */
  useLayoutEffect(() => {
    if (open === null) return
    const flyout = flyoutRef.current
    const bar = rootRef.current
    if (!flyout || !bar) return

    const flyoutBox = flyout.getBoundingClientRect()
    const barBox = bar.getBoundingClientRect()
    // Im inaktiven Tab (`display: none`) misst alles 0 — dort ist nichts zu
    // entscheiden (TabStack-Falle, CLAUDE.md).
    if (flyoutBox.height === 0 || barBox.height === 0) return

    const canvas = bar.closest('.react-flow')
    const bounds = canvas
      ? canvas.getBoundingClientRect()
      : { top: 0, bottom: window.innerHeight }

    const need = flyoutBox.height + FLYOUT_GAP
    const room = {
      above: barBox.top - bounds.top,
      below: bounds.bottom - barBox.bottom,
    }
    const other = placement === 'above' ? 'below' : 'above'
    setFlyoutPlacement(room[placement] >= need || room[other] < need ? placement : other)
  }, [open, placement])

  /*
    Schließen: Escape und ein Klick daneben. Beide hängen nur, SOLANGE etwas
    offen ist — ein dauerhaft lauschender Zettel wäre auch im inaktiven Tab
    wach (TabStack-Falle, CLAUDE.md).

    Escape in der Aufnahmephase und mit `stopPropagation`: Sonst käme es beim
    Canvas an, und der hebt damit die Auswahl auf (`useCanvasEscape`) — ein
    Escape, das den Wähler schließt, soll den Zettel nicht gleich mitnehmen.
  */
  useEffect(() => {
    if (open === null) return

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.stopPropagation()
      setOpen(null)
    }
    const onPointerDown = (event: PointerEvent) => {
      if (rootRef.current?.contains(event.target as Node)) return
      setOpen(null)
    }

    document.addEventListener('keydown', onKeyDown, true)
    document.addEventListener('pointerdown', onPointerDown, true)
    return () => {
      document.removeEventListener('keydown', onKeyDown, true)
      document.removeEventListener('pointerdown', onPointerDown, true)
    }
  }, [open])

  const renderButton = (button: CanvasOptionsBarButton) => {
    switch (button) {
      case 'emoji':
        return (
          <WithTooltip key="emoji" content={t('projectCanvas.note.changeIcon')}>
            <button
              aria-label={t('projectCanvas.note.changeIcon')}
              type="button"
              className="canvas-options-bar__button"
              /*
                Steht da und tut nichts: Sein Wähler ist noch nicht entworfen
                (siehe .claude/tasks/canvas-options-bar-redesign.md). Bestehende
                Emojis bleiben auf ihren Zetteln stehen.
              */
              aria-disabled="true"
              onClick={undefined}
            >
              <SmilePlus size={16} strokeWidth={2} />
            </button>
          </WithTooltip>
        )

      case 'color':
        return (
          <div key="color" className="canvas-options-bar__item">
            <WithTooltip content={t('projectCanvas.note.changeColor')}>
              <button
                aria-label={t('projectCanvas.note.changeColor')}
                type="button"
                className="canvas-options-bar__button canvas-options-bar__button--wide"
                aria-haspopup="listbox"
                aria-expanded={open === 'color'}
                onClick={() => toggle('color')}
              >
                <CanvasNoteDrop fill={chipFill} size={16} className="canvas-options-bar__chip" />
                <Caret />
              </button>
            </WithTooltip>
            {colorPresence.mounted && (
              <CanvasColorPicker
                leaving={colorPresence.leaving}
                ref={flyoutRef}
                kind={colorKind}
                value={uniformColor === INK_DEFAULT ? null : uniformColor}
                defaultSelected={uniformColor === INK_DEFAULT}
                placement={flyoutPlacement}
                onPreview={colorPreviewHandler}
                onPick={key => { pickDone(); onColorChange(key); setOpen(null); onActionDone?.() }}
                onPickDefault={() => { pickDone(); onColorReset?.(); setOpen(null); onActionDone?.() }}
              />
            )}
          </div>
        )

      case 'font':
        return (
          <div key="font" className="canvas-options-bar__item">
            <WithTooltip content={t('projectCanvas.note.changeFont')}>
              <button
                aria-label={t('projectCanvas.note.changeFont')}
                type="button"
                className="canvas-options-bar__button canvas-options-bar__button--font"
                aria-haspopup="listbox"
                aria-expanded={open === 'font'}
                onClick={() => toggle('font')}
              >
                <span className="canvas-options-bar__font-label" style={fontStyle}>{fontLabel}</span>
                <ChevronsUpDown size={16} strokeWidth={2} />
              </button>
            </WithTooltip>
            {fontPresence.mounted && (
              <CanvasFontMenu
                leaving={fontPresence.leaving}
                ref={flyoutRef}
                value={uniformFont}
                placement={flyoutPlacement}
                onPreview={fontPreviewHandler}
                onPick={key => { pickDone(); onFontChange(key); setOpen(null); onActionDone?.() }}
              />
            )}
          </div>
        )

      case 'size':
        return (
          <div key="size" className="canvas-options-bar__item">
            <WithTooltip content={t('projectCanvas.text.changeSize')}>
              <button
                aria-label={t('projectCanvas.text.changeSize')}
                type="button"
                className="canvas-options-bar__button canvas-options-bar__button--font"
                aria-haspopup="listbox"
                aria-expanded={open === 'size'}
                onClick={() => toggle('size')}
              >
                <span className="canvas-options-bar__font-label">{sizeLabel}</span>
                <ChevronsUpDown size={16} strokeWidth={2} />
              </button>
            </WithTooltip>
            {sizePresence.mounted && (
              <CanvasTextSizeMenu
                leaving={sizePresence.leaving}
                ref={flyoutRef}
                value={uniformSize}
                placement={flyoutPlacement}
                onPick={size => { onFontSizeChange?.(size); setOpen(null); onActionDone?.() }}
                onStep={size => onFontSizeChange?.(size)}
              />
            )}
          </div>
        )

      case 'marks':
        return (
          <div key="marks" className="canvas-options-bar__group">
            {MARK_BUTTONS.map(({ mark, icon: Icon, shortcut }) => {
              const on = Boolean(textFormat?.[mark])
              return (
                <WithTooltip key={mark} content={t(`projectCanvas.text.${mark}`)} shortcut={shortcut}>
                  <button
                    aria-label={t(`projectCanvas.text.${mark}`)}
                    type="button"
                    className={`canvas-options-bar__button${on ? ' is-active' : ''}`}
                    aria-pressed={on}
                    onClick={() => { onTextFormat?.({ kind: 'mark', mark }); onActionDone?.() }}
                  >
                    <Icon size={16} strokeWidth={2} />
                  </button>
                </WithTooltip>
              )
            })}
          </div>
        )

      case 'highlight': {
        const highlight = textFormat?.highlight
        return (
          <div key="highlight" className="canvas-options-bar__item">
            <WithTooltip content={t('projectCanvas.text.highlight')}>
              <button
                aria-label={t('projectCanvas.text.highlight')}
                type="button"
                className="canvas-options-bar__button canvas-options-bar__button--wide"
                aria-haspopup="listbox"
                aria-expanded={open === 'highlight'}
                onClick={() => toggle('highlight')}
              >
                {/* Der Strich unter dem Stift zeigt die Farbe, die gerade markiert. */}
                <span
                  className={`canvas-options-bar__highlight${highlight ? ' canvas-options-bar__highlight--on' : ''}`}
                  style={highlight ? { '--highlight': getCanvasTextHighlight(highlight) } as React.CSSProperties : undefined}
                >
                  <Highlighter size={16} strokeWidth={2} />
                </span>
                <Caret />
              </button>
            </WithTooltip>
            {highlightPresence.mounted && (
              <CanvasColorPicker
                leaving={highlightPresence.leaving}
                ref={flyoutRef}
                kind="highlight"
                value={highlight ?? null}
                defaultSelected={highlight === null}
                placement={flyoutPlacement}
                onPreview={highlightPreviewHandler}
                onPick={color => { pickDone(); onTextFormat?.({ kind: 'highlight', color }); setOpen(null); onActionDone?.() }}
                onPickDefault={() => { pickDone(); onTextFormat?.({ kind: 'highlight', color: null }); setOpen(null); onActionDone?.() }}
                // Mischen lässt den Wähler offen: man mischt, bis es gefällt.
                onShuffle={() => { onTextFormat?.({ kind: 'shuffleMarkers' }); onActionDone?.() }}
                shuffleDisabled={!textFormat?.hasMarkers}
              />
            )}
          </div>
        )
      }

      case 'list': {
        const on = Boolean(textFormat?.list)
        return (
          <WithTooltip key="list" content={t('projectCanvas.text.list')}>
            <button
              aria-label={t('projectCanvas.text.list')}
              type="button"
              className={`canvas-options-bar__button${on ? ' is-active' : ''}`}
              aria-pressed={on}
              onClick={() => { onTextFormat?.({ kind: 'list' }); onActionDone?.() }}
            >
              <List size={16} strokeWidth={2} />
            </button>
          </WithTooltip>
        )
      }

      case 'align': {
        const align = textFormat?.align ?? 'left'
        const AlignIcon = CANVAS_TEXT_ALIGNS.find(entry => entry.key === align)!.icon
        return (
          <div key="align" className="canvas-options-bar__item">
            <WithTooltip content={t('projectCanvas.text.align')}>
              <button
                aria-label={t('projectCanvas.text.align')}
                type="button"
                className="canvas-options-bar__button canvas-options-bar__button--wide"
                aria-haspopup="listbox"
                aria-expanded={open === 'align'}
                onClick={() => toggle('align')}
              >
                <AlignIcon size={16} strokeWidth={2} />
                <Caret />
              </button>
            </WithTooltip>
            {alignPresence.mounted && (
              <CanvasTextAlignMenu
                leaving={alignPresence.leaving}
                ref={flyoutRef}
                value={align}
                placement={flyoutPlacement}
                onPick={next => { onTextFormat?.({ kind: 'align', align: next }); setOpen(null); onActionDone?.() }}
              />
            )}
          </div>
        )
      }

      case 'title':
        return (
          <WithTooltip key="title" content={t('projectCanvas.note.toggleTitle')}>
            <button
              aria-label={t('projectCanvas.note.toggleTitle')}
              type="button"
              className={`canvas-options-bar__button${titleOn ? ' is-active' : ''}`}
              aria-pressed={titleOn}
              onClick={() => { onToggleTitle?.(); onActionDone?.() }}
            >
              <Type size={16} strokeWidth={2} />
            </button>
          </WithTooltip>
        )

      case 'author':
        return (
          <WithTooltip key="author" content={t('projectCanvas.note.toggleAuthor')}>
            <button
              aria-label={t('projectCanvas.note.toggleAuthor')}
              type="button"
              className={`canvas-options-bar__button${authorOn ? ' is-active' : ''}`}
              aria-pressed={authorOn}
              onClick={() => { onToggleAuthor?.(); onActionDone?.() }}
            >
              <CircleUser size={16} strokeWidth={2} />
            </button>
          </WithTooltip>
        )

      case 'apply':
        // Ohne Rückruf kein Knopf — und kein Trenner davor (siehe unten).
        if (!onApply) return null
        return (
          <WithTooltip key="apply" content={applyTitle}>
            <button
              type="button"
              className="canvas-options-bar__button canvas-options-bar__button--apply"
              aria-label={applyTitle}
              // Tot statt folgenlos (CONTEXT.md, Übernehmen).
              disabled={applyState !== 'ready'}
              aria-disabled={applyState !== 'ready'}
              onClick={() => { onApply(); onActionDone?.() }}
            >
              <CanvasApplyTasksIcon size={16} />
            </button>
          </WithTooltip>
        )

      case 'tape':
        // Ohne Rückruf kein Knopf — und kein Trenner davor (siehe unten).
        if (!onTape) return null
        return (
          <WithTooltip key="tape" content={t('projectCanvas.note.tape')}>
            <button
              type="button"
              className="canvas-options-bar__button"
              aria-label={t('projectCanvas.note.tape')}
              onClick={() => { onTape(); onActionDone?.() }}
            >
              <Pin size={16} strokeWidth={2} />
            </button>
          </WithTooltip>
        )

      case 'retape':
        if (!onRetape) return null
        return (
          <WithTooltip key="retape" content={t('projectCanvas.note.retape')}>
            <button
              type="button"
              className="canvas-options-bar__button"
              aria-label={t('projectCanvas.note.retape')}
              onClick={() => { onRetape(); onActionDone?.() }}
            >
              <Shuffle size={16} strokeWidth={2} />
            </button>
          </WithTooltip>
        )

      case 'untape':
        if (!onUntape) return null
        return (
          <WithTooltip key="untape" content={t('projectCanvas.note.untape')}>
            <button
              type="button"
              className="canvas-options-bar__button"
              aria-label={t('projectCanvas.note.untape')}
              onClick={() => { onUntape(); onActionDone?.() }}
            >
              <PinOff size={16} strokeWidth={2} />
            </button>
          </WithTooltip>
        )

      case 'delete':
        return (
          <WithTooltip key="delete" content={t('projectCanvas.note.delete')} shortcut={{ kind: 'character', text: '⌫' }}>
            <button
              aria-label={t('projectCanvas.note.delete')}
              type="button"
              className="canvas-options-bar__button"
              onClick={() => { onDelete(); onActionDone?.() }}
            >
              <Trash2 size={16} strokeWidth={2} />
            </button>
          </WithTooltip>
        )
    }
  }

  const applyTitle = applyState === 'done'
    ? t('projectCanvas.note.applyDone')
    : applyState === 'empty'
      ? t('projectCanvas.note.applyEmpty')
      : applyCount > 1
        ? t('projectCanvas.note.applyMany', { count: applyCount })
        : t('projectCanvas.note.apply')

  return (
    <div
      ref={rootRef}
      // `nopan`: React Flows Zoom-Filter lässt Elemente mit dieser Klasse aus —
      // d3-zoom sähe sonst jedes `mousedown` hier als möglichen Beginn eines
      // Pannens und hielte es vor React auf (`stopImmediatePropagation`).
      className={`canvas-options-bar nopan${className ? ` ${className}` : ''}`}
      data-leaving={leaving ? '' : undefined}
      style={{
        '--bar-surface': bar.surface,
        '--bar-divider': bar.divider,
        '--bar-fg': bar.fg,
        '--bar-chip-border': bar.chipBorder,
      } as React.CSSProperties}
      // Der Zettel darunter darf sich nicht mitbewegen, wenn hier gezogen wird:
      // React Flow nimmt jeden Pointer-Druck im Node als Beginn eines Verschiebens.
      onPointerDown={event => event.stopPropagation()}
      // Kein Fokuswechsel durch den Klick: Sonst verlöre die Textarea eines
      // Zettels im Bearbeiten-Modus den Fokus, ihr `blur` schlösse den Modus,
      // und ein Farbwechsel beim Schreiben würde zum Schreib-Ende. Die Knöpfe
      // bleiben per Tastatur erreichbar; nur die Maus lässt den Fokus, wo er ist.
      onMouseDown={event => event.preventDefault()}
      onClick={event => event.stopPropagation()}
    >
      {buttons
        .filter(button => button !== 'apply' || onApply)
        .filter(button => button !== 'tape' || onTape)
        .filter(button => button !== 'retape' || onRetape)
        .filter(button => button !== 'untape' || onUntape)
        .map((button, index) => (
          <React.Fragment key={button}>
            {index > 0 && <span className="canvas-options-bar__divider" aria-hidden="true" />}
            {renderButton(button)}
          </React.Fragment>
        ))}
    </div>
  )
}
